import { useState } from 'react';

import { LoaderSpinner, SidePanelContentSlot, StyledFlex } from '@dreryk/react-components';

import { ContinuousPanel } from './continuous/ContinuousPanel';
import { DictationPanel } from './dictation/DictationPanel';
import { ElevenLabsDebugPanel } from './components/ElevenLabsDebugPanel';
import { ModeToggle } from './components/ModeToggle';
import { QuotaIndicator } from './components/QuotaIndicator';
import { useAiAssistantPanel } from './AiAssistantSidePanelContentSlot.hooks';
import { useTranscriptionQuota } from './transcription/useTranscriptionQuota';
import type { Field } from './types';
import { AiAssistantSidePanelTitle } from './AiAssistantSidePanelTitle';

type AiAssistantSidePanelContentSlotProps = {
   readonly onAppendToField?: (field: Field, text: string) => void;
   readonly getFieldHtml?: (field: Field) => string;
   readonly setFieldHtml?: (field: Field, html: string) => void;
   readonly setLockedField?: (field: Field | null) => void;
};

// eslint-disable-next-line max-lines-per-function
export const AiAssistantSidePanelContentSlot = (props: AiAssistantSidePanelContentSlotProps): JSX.Element => {
   const panel = useAiAssistantPanel();
   const { loading, data } = useTranscriptionQuota();

   const [isDictationRecording, setIsDictationRecording] = useState(false);
   const [isContinuousRecording, setIsContinuousRecording] = useState(false);
   const isAnyRecording = isDictationRecording || isContinuousRecording;

   if (loading || !data) {
      return <LoaderSpinner />;
   }

   const notConfigured = data.limit === 0;
   const quotaExhausted = data.remainingMinutes <= 0 && !notConfigured;

   const activeElevenlabsConfig =
      panel.mode === 'DICTATION' ? panel.elevenlabsConfig.dictationConfig : panel.elevenlabsConfig.continuousConfig;
   const setActiveElevenlabsConfig =
      panel.mode === 'DICTATION'
         ? panel.elevenlabsConfig.setDictationConfig
         : panel.elevenlabsConfig.setContinuousConfig;

   return (
      <SidePanelContentSlot headerText={<AiAssistantSidePanelTitle />}>
         <StyledFlex direction={'column'} gap={16}>
            <QuotaIndicator
               remainingMinutes={data.remainingMinutes}
               nearLimit={false}
               notConfigured={notConfigured}
               isQuotaExhausted={quotaExhausted}
            />
            <ElevenLabsDebugPanel
               config={activeElevenlabsConfig}
               onChange={setActiveElevenlabsConfig}
               disabled={isAnyRecording}
            />
            <ModeToggle mode={panel.mode} onModeChange={panel.setMode} disabled={isAnyRecording} />
            {panel.mode === 'DICTATION' ? (
               <DictationPanel
                  isQuotaExhausted={quotaExhausted}
                  config={activeElevenlabsConfig}
                  onRecordingChange={setIsDictationRecording}
                  onAppendToField={props.onAppendToField}
                  getFieldHtml={props.getFieldHtml}
                  setFieldHtml={props.setFieldHtml}
                  setLockedField={props.setLockedField}
               />
            ) : (
               <ContinuousPanel
                  isQuotaExhausted={quotaExhausted}
                  config={activeElevenlabsConfig}
                  onRecordingChange={setIsContinuousRecording}
                  onAppendToField={props.onAppendToField}
               />
            )}
         </StyledFlex>
      </SidePanelContentSlot>
   );
};
